import type { TextareaHTMLAttributes } from 'react'
import React, { forwardRef, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { InputTextBase } from 'LuxKit/Typography/InputText'
import { TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from 'LuxKit/Typography/Typography'

const AutoGrowWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  position: relative;
  border: none;
  background: transparent;

  ${InputTextBase};
  line-height: var(${TYPOGRAPHY_LINE_HEIGHT_CSS_VAR});

  &::after {
    /* Note the weird space! Needed to prevent jumpy behavior */
    content: attr(data-replicated-value) ' ';
    /* This is how textarea text behaves */
    white-space: pre-wrap;
    /* Hidden from view, clicks, and screen readers */
    visibility: hidden;
    padding: ${rem(6)} 0;
    border: none;
    overflow-wrap: anywhere;
    grid-area: 1 / 1;
  }

  /* Place on top of each other */
  & > textarea {
    grid-area: 1 / 1;
    border: none;
    overflow: hidden;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
`

const StyledTextArea = styled.textarea`
  ${InputTextBase};
  line-height: var(${TYPOGRAPHY_LINE_HEIGHT_CSS_VAR});
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  padding: ${rem(6)} 0;
  min-height: ${rem(20)};
  overflow: hidden;

  &::-ms-clear {
    display: none;
  }

  &::placeholder {
    color: ${props => props.theme.palette.neutral.default.four};
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &:disabled {
    cursor: not-allowed;

    &::placeholder {
      color: ${props => props.theme.palette.neutral.default.two};
    }
  }

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`

interface Props extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'style'> {
  inputClassName?: string
}

/**
 * A borderless, auto-growing textarea component.
 *
 * Unlike TextAreaOnly, this component does NOT use InputWrap, so it has:
 * - No border
 * - No validation spacing
 * - No validation messages
 * - No icon slots
 *
 * Ideal for inline/embedded comment inputs where the container provides its own styling.
 */
const BorderlessTextAreaOnly = forwardRef<HTMLTextAreaElement | null, Props>((props, ref) => {
  const {
    value,
    defaultValue,
    className,
    inputClassName,
    disabled,
    ...inputProps
  } = props

  const { onChange: propsOnChange, ...restInputProps } = inputProps

  const [currentValue, setCurrentValue] = useState(value ?? defaultValue ?? '')

  useEffect(() => {
    setCurrentValue(value ?? defaultValue ?? '')
  }, [value, defaultValue])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentValue(e.currentTarget.value)
    propsOnChange?.(e)
  }, [propsOnChange])

  return (
    <AutoGrowWrapper className={className} data-replicated-value={currentValue}>
      <StyledTextArea
        {...restInputProps}
        ref={ref}
        value={value}
        defaultValue={defaultValue}
        disabled={disabled}
        className={inputClassName}
        onChange={handleChange}
        rows={1}
      />
    </AutoGrowWrapper>
  )
})

BorderlessTextAreaOnly.displayName = 'BorderlessTextAreaOnly'

export default BorderlessTextAreaOnly
